import PropTypes from 'prop-types';
import {
  AFFILIATION_DEALER,
  PAGE_BREAKPOINTS,
  STATUS,
  USER_TYPE_GUEST
} from '@app/constants/commonConstants';
import { useBreakpoint } from '@app/hooks';
import { AlloyButton, InlineLoader, PrintIcon } from 'cat-ecommerce-alloy';
import { useTranslation } from 'react-i18next';
import ShowNotesToggle from '../ShowNotesToggle';
import cx from 'classnames';
import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { updatePreference } from '@app/store/shoppingPreferences/actions';
import { useFormikContext } from 'formik';
import styles from './ShoppingCartPageSubHeader.module.scss';

const ShoppingCartPageSubHeader = ({ numItemsInCart }) => {
  const { values } = useFormikContext();
  const dispatch = useDispatch();

  const [t] = useTranslation();
  const printLabel = t('PD_PRINT');
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);
  const getCommonVarsStatus = useSelector(s => s.common.getCommonVarsStatus);
  const shoppingPreferencesError = useSelector(
    s => s.shoppingPreferences.error
  );
  const isRequiredFieldsLoading = useSelector(
    s => s.checkout.isRequiredFieldsLoading
  );
  const shoppingPreferenceDataStatus = useSelector(
    s => s.shoppingPreferences.shoppingPreferenceDataStatus
  );
  const getCurrentOrderStatus = useSelector(
    s => s.orders.getCurrentOrderStatus
  );
  const getDealerCustomerStoresStatus = useSelector(
    s => s.dealerCustomer.getDealerCustomerStoresStatus
  );
  const isOrderPriceAvailabilityLoading = useSelector(
    s => s.orders.isOrderPriceAvailabilityLoading
  );
  const registrationPendingApproval = useSelector(
    s => s.common?.registrationPendingApproval
  );
  const customerType = useSelector(
    s => s.shoppingPreferences.shoppingPreferencesData.customerType
  );
  const isInstantAccess = useSelector(s => s.common.isInstantAccess);
  const userAffiliation = useSelector(s => s.common.userAffiliation);

  //if cart is empty
  const displayLineItemNotes = useSelector(
    s => s.shoppingPreferences?.shoppingPreferencesData?.displayLineItemNotes
  );
  const ascendingOrderItems = useSelector(
    s => s.orders?.byId[s.orders?.current]?.ascendingOrderItems || []
  );
  const updateUserPreferences = () => {
    if (
      ascendingOrderItems.length === 0 &&
      'showNotes' in values &&
      displayLineItemNotes !== values?.showNotes
    ) {
      dispatch(
        updatePreference({ shoppingCartAddNotes: values?.showNotes }, false)
      );
    }
  };
  useEffect(() => {
    window.addEventListener('beforeunload', updateUserPreferences);
    return () => {
      window.removeEventListener('beforeunload', updateUserPreferences);
    };
  }, [dispatch, values, ascendingOrderItems]);

  const isDealerCustomerLoading =
    getDealerCustomerStoresStatus === STATUS.PENDING ||
    getDealerCustomerStoresStatus === STATUS.IDLE;
  const isOrdersLoading =
    getCurrentOrderStatus === STATUS.PENDING ||
    getCurrentOrderStatus === STATUS.IDLE;
  const shoppingPreferencesLoading =
    shoppingPreferenceDataStatus === STATUS.PENDING ||
    shoppingPreferenceDataStatus === STATUS.IDLE;
  const userType = useSelector(state => state.common.userType);
  const isLoggedIn = !!(userType && userType !== USER_TYPE_GUEST);
  const isDealerUser = userAffiliation === AFFILIATION_DEALER;
  const isGuestUser = userType && userType === USER_TYPE_GUEST;
  const pendingApprovalUser =
    registrationPendingApproval ||
    (customerType === undefined &&
      isInstantAccess &&
      !isGuestUser &&
      !isDealerUser &&
      !registrationPendingApproval);
  const showNotesVisible =
    isLoggedIn && !pendingApprovalUser && isMD && !shoppingPreferencesError;
  const showPrintButton = !!(isLoggedIn || numItemsInCart);
  const guestLoadingStates =
    isRequiredFieldsLoading ||
    isOrdersLoading ||
    (isLoggedIn && !pendingApprovalUser && isDealerCustomerLoading) ||
    isOrderPriceAvailabilityLoading ||
    getCommonVarsStatus === STATUS.PENDING;
  const shoppingCartButtonDisabled = isLoggedIn
    ? shoppingPreferencesLoading || guestLoadingStates
    : guestLoadingStates;
  const hideInlineLoader = !guestLoadingStates && !shoppingPreferencesLoading;
  return (
    <div
      className={cx('d-flex d-print-none mb-3 mt-2', {
        'justify-content-between': showNotesVisible,
        'justify-content-end': !showNotesVisible
      })}
    >
      {showNotesVisible &&
        (hideInlineLoader ? (
          <ShowNotesToggle />
        ) : (
          <InlineLoader className="py-2" />
        ))}
      {showPrintButton && (
        <AlloyButton
          aria-label={printLabel}
          buttonType="ghost"
          disabled={shoppingCartButtonDisabled}
          size="dynamic"
          className="px-0 u-fs--16 d-inline-flex align-items-center me-lg-0"
          onClick={() => window.print()}
        >
          <PrintIcon
            title=""
            aria-label={`${printLabel}-icon`}
            width={16}
            height={16}
            className="d-none d-md-inline-block me-2"
          />
          <span className={styles['print-label']}>{printLabel}</span>
        </AlloyButton>
      )}
    </div>
  );
};

export default ShoppingCartPageSubHeader;

ShoppingCartPageSubHeader.propTypes = {
  numItemsInCart: PropTypes.number.isRequired
};
